import { useEffect, useMemo, useState } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { AddressAutocomplete, type AddressSearchResult } from '@/components/shared/AddressAutocomplete'
import type { GuestSpot } from '@/types/models'
import { Loader2, Upload, X } from 'lucide-react'
import { deleteField } from 'firebase/firestore'
import {
  uploadLocalGuestSpotProfileImage,
  uploadLocalGuestSpotTattooImage,
} from '@/lib/localGuestSpotAdminUpload'

interface GuestSpotEditModalProps {
  guestSpot?: GuestSpot
  defaultTargetCity?: string
  isOpen: boolean
  onClose: () => void
  onSave: (updatedData: Partial<GuestSpot>, overriddenFieldNames?: string[]) => Promise<void>
}

interface TattooImageSlotState {
  previewUrl: string | null
  pendingUploadFile: File | null
}

const TATTOO_IMAGE_SLOT_COUNT = 3

function formatDateInputValue(dateValue: Date | undefined): string {
  if (!dateValue) {
    return new Date().toISOString().split('T')[0]
  }

  return dateValue instanceof Date
    ? dateValue.toISOString().split('T')[0]
    : new Date(dateValue).toISOString().split('T')[0]
}

function buildInitialTattooImageSlots(guestSpot: GuestSpot | undefined): TattooImageSlotState[] {
  const existingImageUrls =
    guestSpot?.top4CompressedImageUrls?.length
      ? guestSpot.top4CompressedImageUrls
      : guestSpot?.tattooImageUrls ?? []

  return Array.from({ length: TATTOO_IMAGE_SLOT_COUNT }, (_, slotIndex) => ({
    previewUrl: existingImageUrls[slotIndex] ?? null,
    pendingUploadFile: null,
  }))
}

export function GuestSpotEditModal({
  guestSpot,
  defaultTargetCity,
  isOpen,
  onClose,
  onSave,
}: GuestSpotEditModalProps) {
  const isCreateMode = !guestSpot
  const [isSaving, setIsSaving] = useState(false)
  const [isUploadingImages, setIsUploadingImages] = useState(false)

  const [location, setLocation] = useState('')
  const [homeCity, setHomeCity] = useState('')
  const [homeCountry, setHomeCountry] = useState('')
  const [homeLat, setHomeLat] = useState<number | undefined>()
  const [homeLon, setHomeLon] = useState<number | undefined>()

  const [targetLocation, setTargetLocation] = useState('')
  const [targetCity, setTargetCity] = useState('')
  const [targetCountry, setTargetCountry] = useState('')
  const [targetLat, setTargetLat] = useState<number | undefined>()
  const [targetLon, setTargetLon] = useState<number | undefined>()

  const [username, setUsername] = useState('')
  const [targetStudioUsername, setTargetStudioUsername] = useState('')
  const [studioName, setStudioName] = useState('')
  const [startDate, setStartDate] = useState(formatDateInputValue(undefined))
  const [endDate, setEndDate] = useState(formatDateInputValue(undefined))

  const [profileImageFile, setProfileImageFile] = useState<File | null>(null)
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(null)
  const [tattooImageSlots, setTattooImageSlots] = useState<TattooImageSlotState[]>(() =>
    buildInitialTattooImageSlots(guestSpot),
  )

  useEffect(() => {
    if (!isOpen) return

    setLocation(
      guestSpot?.homeCity && guestSpot?.homeCountry
        ? `${guestSpot.homeCity}, ${guestSpot.homeCountry}`
        : '',
    )
    setHomeCity(guestSpot?.homeCity || '')
    setHomeCountry(guestSpot?.homeCountry || '')
    setHomeLat(guestSpot?.homeLat)
    setHomeLon(guestSpot?.homeLon)

    setTargetLocation(
      guestSpot?.targetCity && guestSpot?.targetCountry
        ? `${guestSpot.targetCity}, ${guestSpot.targetCountry}`
        : defaultTargetCity || '',
    )
    setTargetCity(guestSpot?.targetCity || defaultTargetCity || '')
    setTargetCountry(guestSpot?.targetCountry || '')
    setTargetLat(guestSpot?.targetLat)
    setTargetLon(guestSpot?.targetLon)

    setUsername(guestSpot?.username || '')
    setTargetStudioUsername(guestSpot?.targetStudioUsername || guestSpot?.sourceStudioUsername || '')
    setStudioName(guestSpot?.studioName || '')
    setStartDate(formatDateInputValue(guestSpot?.startDate))
    setEndDate(formatDateInputValue(guestSpot?.endDate))

    setProfileImageFile(null)
    setProfileImagePreview(guestSpot?.profileImageUrl || null)
    setTattooImageSlots(buildInitialTattooImageSlots(guestSpot))
  }, [defaultTargetCity, guestSpot, isOpen])

  const usernameForUploads = useMemo(
    () => username.replace(/^@+/, '').trim() || guestSpot?.username?.replace(/^@+/, '').trim() || '',
    [guestSpot?.username, username],
  )

  const handleHomeAddressSelect = (place: AddressSearchResult) => {
    setLocation(place.address)

    const addressParts = place.address.split(', ')
    if (addressParts.length >= 2) {
      const country = addressParts[addressParts.length - 1]
      const city = addressParts[addressParts.length - 2].replace(/^\d+\s+/, '')
      setHomeCity(city)
      setHomeCountry(country)
    }

    setHomeLat(place.lat)
    setHomeLon(place.lon)
  }

  const handleTargetAddressSelect = (place: AddressSearchResult) => {
    setTargetLocation(place.address)

    const addressParts = place.address.split(', ')
    if (addressParts.length >= 2) {
      const country = addressParts[addressParts.length - 1]
      const city = addressParts[addressParts.length - 2].replace(/^\d+\s+/, '')
      setTargetCity(city)
      setTargetCountry(country)
    }

    setTargetLat(place.lat)
    setTargetLon(place.lon)
  }

  const handleProfileImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]
    if (!selectedFile) return

    if (!selectedFile.type.startsWith('image/')) {
      alert('Please select an image file')
      return
    }

    if (selectedFile.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB')
      return
    }

    setProfileImageFile(selectedFile)

    const reader = new FileReader()
    reader.onloadend = () => {
      setProfileImagePreview(reader.result as string)
    }
    reader.readAsDataURL(selectedFile)
  }

  const handleTattooImageSelect = (slotIndex: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]
    if (!selectedFile) return

    if (!selectedFile.type.startsWith('image/')) {
      alert('Please select an image file')
      return
    }

    if (selectedFile.size > 8 * 1024 * 1024) {
      alert('Image size must be less than 8MB')
      return
    }

    const reader = new FileReader()
    reader.onloadend = () => {
      setTattooImageSlots((previousSlots) =>
        previousSlots.map((slot, index) =>
          index === slotIndex
            ? {
                previewUrl: reader.result as string,
                pendingUploadFile: selectedFile,
              }
            : slot,
        ),
      )
    }
    reader.readAsDataURL(selectedFile)
  }

  const handleClearTattooImageSlot = (slotIndex: number) => {
    setTattooImageSlots((previousSlots) =>
      previousSlots.map((slot, index) =>
        index === slotIndex
          ? {
              previewUrl: null,
              pendingUploadFile: null,
            }
          : slot,
      ),
    )
  }

  const handleSave = async () => {
    if (!targetCity || !targetCountry || !startDate || !endDate) {
      alert('Please fill in all required fields: Target Location and Dates')
      return
    }

    if (isCreateMode && !username) {
      alert('Username is required when creating a new guest spot')
      return
    }

    if (!isCreateMode && !usernameForUploads) {
      alert('This guest spot is missing a username, which is required for image uploads')
      return
    }

    setIsSaving(true)
    setIsUploadingImages(true)

    try {
      const updatedData: Partial<GuestSpot> = {
        targetCity,
        targetCountry,
        startDate: new Date(startDate),
        endDate: new Date(endDate),
      }
      const overriddenFieldNames: string[] = [
        'targetCity',
        'targetCountry',
        'startDate',
        'endDate',
      ]

      if (targetLat !== undefined && targetLon !== undefined) {
        updatedData.targetLat = targetLat
        updatedData.targetLon = targetLon
        overriddenFieldNames.push('targetLat', 'targetLon')
      } else if (!isCreateMode) {
        updatedData.targetLat = deleteField() as unknown as number
        updatedData.targetLon = deleteField() as unknown as number
        overriddenFieldNames.push('targetLat', 'targetLon')
      }

      if (isCreateMode) {
        updatedData.username = username.replace(/^@+/, '').trim().toLowerCase()
        overriddenFieldNames.push('username')
      }

      if (targetStudioUsername.trim()) {
        updatedData.targetStudioUsername = targetStudioUsername.replace(/^@+/, '').trim().toLowerCase()
        overriddenFieldNames.push('targetStudioUsername')
      } else if (!isCreateMode && (guestSpot?.targetStudioUsername || guestSpot?.sourceStudioUsername)) {
        updatedData.targetStudioUsername = deleteField() as unknown as string
        overriddenFieldNames.push('targetStudioUsername')
      }

      if (homeCity.trim()) {
        updatedData.homeCity = homeCity.trim()
        overriddenFieldNames.push('homeCity')
      } else if (!isCreateMode && guestSpot?.homeCity) {
        updatedData.homeCity = deleteField() as unknown as string
        overriddenFieldNames.push('homeCity')
      }

      if (homeCountry.trim()) {
        updatedData.homeCountry = homeCountry.trim()
        overriddenFieldNames.push('homeCountry')
      } else if (!isCreateMode && guestSpot?.homeCountry) {
        updatedData.homeCountry = deleteField() as unknown as string
        overriddenFieldNames.push('homeCountry')
      }

      if (homeLat !== undefined && homeLon !== undefined) {
        updatedData.homeLat = homeLat
        updatedData.homeLon = homeLon
        overriddenFieldNames.push('homeLat', 'homeLon')
      } else if (!isCreateMode && (guestSpot?.homeLat !== undefined || guestSpot?.homeLon !== undefined)) {
        updatedData.homeLat = deleteField() as unknown as number
        updatedData.homeLon = deleteField() as unknown as number
        overriddenFieldNames.push('homeLat', 'homeLon')
      }

      if (studioName.trim()) {
        updatedData.studioName = studioName.trim()
        overriddenFieldNames.push('studioName')
      } else if (!isCreateMode && guestSpot?.studioName) {
        updatedData.studioName = deleteField() as unknown as string
        overriddenFieldNames.push('studioName')
      }

      if (profileImageFile && usernameForUploads) {
        updatedData.profileImageUrl = await uploadLocalGuestSpotProfileImage(
          profileImageFile,
          usernameForUploads,
        )
        overriddenFieldNames.push('profileImageUrl')
      } else if (!isCreateMode && !profileImagePreview && guestSpot?.profileImageUrl) {
        updatedData.profileImageUrl = deleteField() as unknown as string
        overriddenFieldNames.push('profileImageUrl')
      }

      const resolvedTattooImageUrls: string[] = []
      for (let slotIndex = 0; slotIndex < tattooImageSlots.length; slotIndex += 1) {
        const tattooImageSlot = tattooImageSlots[slotIndex]

        if (tattooImageSlot.pendingUploadFile && usernameForUploads) {
          const uploadedTattooImageUrl = await uploadLocalGuestSpotTattooImage(
            tattooImageSlot.pendingUploadFile,
            usernameForUploads,
            slotIndex,
          )
          resolvedTattooImageUrls.push(uploadedTattooImageUrl)
          continue
        }

        if (tattooImageSlot.previewUrl && !tattooImageSlot.previewUrl.startsWith('data:')) {
          resolvedTattooImageUrls.push(tattooImageSlot.previewUrl)
        }
      }

      const initialTattooImageUrls = (guestSpot?.top4CompressedImageUrls ?? guestSpot?.tattooImageUrls ?? [])
        .filter((url): url is string => typeof url === 'string' && url.trim().length > 0)
        .slice(0, 3)
      const tattooImagesChanged =
        isCreateMode || JSON.stringify(resolvedTattooImageUrls) !== JSON.stringify(initialTattooImageUrls)

      if (tattooImagesChanged) {
        if (resolvedTattooImageUrls.length > 0) {
          updatedData.tattooImageUrls = resolvedTattooImageUrls
          updatedData.top4CompressedImageUrls = resolvedTattooImageUrls.slice(0, 4)
        } else if (!isCreateMode) {
          updatedData.tattooImageUrls = deleteField() as unknown as string[]
          updatedData.top4CompressedImageUrls = deleteField() as unknown as string[]
        }
        overriddenFieldNames.push('tattooImageUrls', 'top4CompressedImageUrls')
      }

      if (isCreateMode) {
        updatedData.createdAt = new Date()
        updatedData.pipeline = 'local'
        updatedData.spotStatus = 'active'
      }

      await onSave(updatedData, isCreateMode ? undefined : overriddenFieldNames)
      onClose()
    } catch (error) {
      console.error('Failed to save guest spot:', error)
      alert('Failed to save changes. Please try again.')
    } finally {
      setIsSaving(false)
      setIsUploadingImages(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle>{isCreateMode ? 'Create New Guest Spot' : 'Edit Guest Spot'}</DialogTitle>
          <DialogDescription>
            {isCreateMode
              ? 'Fill in the details to create a new guest spot. Required fields are marked with *.'
              : 'Update guest spot details. Saved edits are tracked in manualOverrides so the pipeline will not overwrite them.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="username">Instagram Username {isCreateMode ? '*' : ''}</Label>
            <Input
              id="username"
              value={username}
              onChange={(event) => setUsername(event.target.value.replace('@', ''))}
              placeholder="username (without @)"
              disabled={!isCreateMode}
              required={isCreateMode}
            />
            {!isCreateMode ? (
              <p className="text-xs text-muted-foreground">
                Username is fixed because it is part of the spot fingerprint used by manualOverrides.
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="profileImage">Profile Image</Label>
              {profileImagePreview ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setProfileImageFile(null)
                    setProfileImagePreview(null)
                  }}
                  className="h-auto px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  <X className="mr-1 h-3 w-3" />
                  Clear
                </Button>
              ) : null}
            </div>
            {profileImagePreview ? (
              <img
                src={profileImagePreview}
                alt="Profile preview"
                className="h-24 w-24 rounded-full border-2 border-border object-cover"
              />
            ) : null}
            <Input
              id="profileImage"
              type="file"
              accept="image/*"
              onChange={handleProfileImageSelect}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => document.getElementById('profileImage')?.click()}
              disabled={isSaving || isUploadingImages}
              className="w-full justify-center"
            >
              <Upload className="mr-2 h-4 w-4" />
              {profileImagePreview ? 'Replace Profile Image' : 'Upload Profile Image'}
            </Button>
          </div>

          <div className="space-y-3">
            <Label>Tattoo Images (3 slots)</Label>
            <div className="grid grid-cols-3 gap-2">
              {tattooImageSlots.map((tattooImageSlot, slotIndex) => (
                <div key={slotIndex} className="space-y-2">
                  <div className="relative aspect-square overflow-hidden rounded-xl border border-border bg-muted/40">
                    {tattooImageSlot.previewUrl ? (
                      <img
                        src={tattooImageSlot.previewUrl}
                        alt={`Tattoo preview ${slotIndex + 1}`}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
                        Slot {slotIndex + 1}
                      </div>
                    )}
                  </div>
                  <Input
                    id={`tattoo-image-${slotIndex}`}
                    type="file"
                    accept="image/*"
                    onChange={(event) => handleTattooImageSelect(slotIndex, event)}
                    className="hidden"
                  />
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => document.getElementById(`tattoo-image-${slotIndex}`)?.click()}
                      disabled={isSaving || isUploadingImages}
                    >
                      Upload
                    </Button>
                    {tattooImageSlot.previewUrl ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleClearTattooImageSlot(slotIndex)}
                        disabled={isSaving || isUploadingImages}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Card listing only shows the image row when all 3 compressed image URLs are present.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="targetLocation">Target Location (City, Country) *</Label>
            <AddressAutocomplete
              value={targetLocation}
              onChange={setTargetLocation}
              onSelect={handleTargetAddressSelect}
              placeholder="Start typing the target city..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="targetCity">Target City *</Label>
            <Input
              id="targetCity"
              value={targetCity}
              onChange={(event) => setTargetCity(event.target.value)}
              placeholder="Berlin"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="targetCountry">Target Country *</Label>
            <Input
              id="targetCountry"
              value={targetCountry}
              onChange={(event) => setTargetCountry(event.target.value)}
              placeholder="Germany"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="targetStudioUsername">Guest Studio Instagram</Label>
            <Input
              id="targetStudioUsername"
              value={targetStudioUsername}
              onChange={(event) => setTargetStudioUsername(event.target.value.replace('@', ''))}
              placeholder="studio_username"
            />
          </div>

          <div className="border-t border-border my-4" />

          <div className="space-y-2">
            <Label htmlFor="location">Home Location (City, Country)</Label>
            <AddressAutocomplete
              value={location}
              onChange={setLocation}
              onSelect={handleHomeAddressSelect}
              placeholder="Start typing the home city..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="homeCity">Home City</Label>
            <Input
              id="homeCity"
              value={homeCity}
              onChange={(event) => setHomeCity(event.target.value)}
              placeholder="Berlin"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="homeCountry">Home Country</Label>
            <Input
              id="homeCountry"
              value={homeCountry}
              onChange={(event) => setHomeCountry(event.target.value)}
              placeholder="Germany"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="studioName">Studio Name (Optional)</Label>
            <Input
              id="studioName"
              value={studioName}
              onChange={(event) => setStudioName(event.target.value)}
              placeholder="Studio name"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date *</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">End Date *</Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                required
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || isUploadingImages}>
            {isSaving || isUploadingImages ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isUploadingImages ? 'Uploading images...' : isCreateMode ? 'Creating...' : 'Saving...'}
              </>
            ) : (
              isCreateMode ? 'Create Guest Spot' : 'Save Changes'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
